import { getCurrentScreenLayout, getScreenLayoutPathPrefixOrEmpty } from '../utilities/screenLayout';
import { tryOrNull } from '../utilities/tryOrNull';

const EMBEDDED_LAUNCH_DESTINATION_QUERY_PARAM_NAME = 'launchDestination';
const OAUTH2_STATE_QUERY_PARAM_NAME = 'state';

const STATE_DESTINATION_DELIMITER = '.';

export enum EmbeddedLaunchDestination {
	Quality = 'Quality',
}

const EMBEDDED_LAUNCH_DESTINATION_TO_PATH_SUFFIX = {
	[EmbeddedLaunchDestination.Quality]: 'quality-workspace',
} as const satisfies Readonly<Record<EmbeddedLaunchDestination, string>>;

// Single source of truth for the lowercase wire value used both in the launch
// URL query param and in our own `state` encoding.
const EMBEDDED_LAUNCH_DESTINATION_TO_RAW_VALUE = {
	[EmbeddedLaunchDestination.Quality]: 'quality',
} as const satisfies Readonly<Record<EmbeddedLaunchDestination, string>>;

const EMBEDDED_LAUNCH_DESTINATION_BY_RAW_VALUE: ReadonlyMap<string, EmbeddedLaunchDestination> = new Map(
	Object.values(EmbeddedLaunchDestination).map((destination) => [
		EMBEDDED_LAUNCH_DESTINATION_TO_RAW_VALUE[destination],
		destination,
	]),
);

function sanitizeEmbeddedLaunchDestinationOrThrow(rawValue: string | null): EmbeddedLaunchDestination {
	if (rawValue === null) {
		throw new Error('Cannot sanitize a null embedded launch destination.');
	}
	const destination = EMBEDDED_LAUNCH_DESTINATION_BY_RAW_VALUE.get(rawValue);
	if (destination === undefined) {
		throw new Error(`Unknown embedded launch destination: "${rawValue}".`);
	}
	return destination;
}

const sanitizeEmbeddedLaunchDestinationOrNull = tryOrNull(sanitizeEmbeddedLaunchDestinationOrThrow);

export function getEmbeddedLaunchDestinationFromUrlParamsOrNull(
	urlParams: URLSearchParams,
): EmbeddedLaunchDestination | null {
	const launchDestinationRawValue = urlParams.get(EMBEDDED_LAUNCH_DESTINATION_QUERY_PARAM_NAME);
	return sanitizeEmbeddedLaunchDestinationOrNull(launchDestinationRawValue);
}

/**
 * Encodes a CSRF-random `state` value with an optional launch destination so
 * the destination round-trips through the OAuth2 redirect chain on the
 * standards-defined `state` parameter instead of polluting `redirect_uri`
 * (which is subject to strict allowlist matching at Auth0/Epic).
 *
 * Format: `<random>` when no destination, `<random>.<destination>` otherwise.
 */
export function getEncodedStateWithEmbeddedLaunchDestination(
	randomState: string,
	destination: EmbeddedLaunchDestination | null,
): string {
	if (destination === null) {
		return randomState;
	}
	return `${randomState}${STATE_DESTINATION_DELIMITER}${EMBEDDED_LAUNCH_DESTINATION_TO_RAW_VALUE[destination]}`;
}

function decodeEmbeddedLaunchDestinationFromStateOrThrow(state: string | null): EmbeddedLaunchDestination {
	if (state === null) {
		throw new Error('Auth0/OAuth2 must contain a `state` parameter.');
	}
	if (!state.includes(STATE_DESTINATION_DELIMITER)) {
		throw new Error(`State "${state}" does not contain a destination delimiter.`);
	}
	const [, rawDestination] = state.split(STATE_DESTINATION_DELIMITER);
	return sanitizeEmbeddedLaunchDestinationOrThrow(rawDestination ?? null);
}

const decodeEmbeddedLaunchDestinationFromStateOrNull = tryOrNull(decodeEmbeddedLaunchDestinationFromStateOrThrow);

/**
 * Composes the post-login path for a launch destination by prefixing the
 * current screen layout (e.g. `Embedded` -> `/embedded`) onto the destination
 * suffix (e.g. `quality` -> `quality-workspace`). Returns `null` when the
 * launch URL did not request a destination, in which case the caller should
 * fall through to the existing patient/document/appointment routing.
 *
 * The destination can be conveyed two ways:
 * - On the OAuth2 callback: encoded inside the `state` query param.
 * - On the original launch URL: as a plain `launchDestination` query param.
 *
 * `state` is checked first because that's how the destination travels through
 * the OAuth chain; the plain query param is the fallback for the very first
 * hop (before the standalone has wrapped it into `state`).
 */
export function getEmbeddedLaunchDestinationFullPathOrNull(urlSearch: string): string | null {
	const urlParams = new URLSearchParams(urlSearch);
	const destination =
		decodeEmbeddedLaunchDestinationFromStateOrNull(urlParams.get(OAUTH2_STATE_QUERY_PARAM_NAME)) ??
		getEmbeddedLaunchDestinationFromUrlParamsOrNull(urlParams);

	if (destination === null) {
		return null;
	}

	const suffix = EMBEDDED_LAUNCH_DESTINATION_TO_PATH_SUFFIX[destination];
	const prefix = getScreenLayoutPathPrefixOrEmpty(getCurrentScreenLayout());
	return prefix === '' ? `/${suffix}` : `${prefix}/${suffix}`;
}
